import { TypedDocumentNode } from '@apollo/client';
import { useApolloClient } from '@apollo/client/react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { AccessEvent, MobileOpCode, Query, QueryGetAccessEventsForTenantArgs } from 'graphql/generated/graphqlTypes';
import { GET_ACCESS_EVENTS_FOR_TENANT } from 'graphql/v2/queries';
import { useEffect, useMemo, useState } from 'react';
import { TranslatedHookProps } from 'v2/hooks/hooks.types';

dayjs.extend(utc);

// Typing the documents (instead of passing generics to the Apollo hooks) avoids the deprecated hook signatures.
// The pages are fetched with `client.query` rather than `useLazyQuery`: the lazy query's observable rejected
// the in-flight request with an `AbortError` while the dashboard was still mounted.
const GET_ACCESS_EVENTS_FOR_TENANT_TYPED: TypedDocumentNode<
  Pick<Query, 'getAccessEventsForTenant'>,
  QueryGetAccessEventsForTenantArgs
> = GET_ACCESS_EVENTS_FOR_TENANT;

const MINUTES_PER_HOUR = 60;

/** How much of the past the window covers. */
export const WINDOW_HOURS = 24;
/** Each point on the chart counts the events that happened inside one of these slots. */
export const BUCKET_MINUTES = 30;
/** Lets a consumer translate an hour-based tick spacing into a number of buckets. */
export const BUCKETS_PER_HOUR = MINUTES_PER_HOUR / BUCKET_MINUTES;
export const BUCKET_COUNT = WINDOW_HOURS * BUCKETS_PER_HOUR;

export interface AccessEventBucket {
  /** Start of the slot this bucket covers, as an ISO string — format it with `formatDate`. */
  timestamp: string;
  granted: number;
  denied: number;
}

// The newest bucket is the slot the current minute falls into, so it fills up as time passes rather
// than starting a new one on every render.
const getCurrentBucketStart = () => {
  const now = dayjs().startOf('minute');

  return now.subtract(now.minute() % BUCKET_MINUTES, 'minute');
};

// the window ends with the current slot, so it starts BUCKET_COUNT - 1 slots earlier
const getWindowStart = () => getCurrentBucketStart().subtract((BUCKET_COUNT - 1) * BUCKET_MINUTES, 'minute');

// fixed at effect start so every page of the loop below queries the exact same window
const getWindowDateRange = () => ({
  rangeStart: getWindowStart().utc().format(),
  rangeEnd: dayjs().utc().format()
});

const useRecentAccessEvents = ({ handleFetchError, translation }: TranslatedHookProps) => {
  const [accessEvents, setAccessEvents] = useState<AccessEvent[]>([]);
  const [loading, setLoading] = useState(true);

  const client = useApolloClient();

  useEffect(() => {
    // Unmounting mid-fetch abandons the paging loop: without this the remaining pages would keep
    // being requested, and their state setters would fire, for data nobody will see.
    let cancelled = false;

    (async () => {
      setLoading(true);

      const dateRange = getWindowDateRange();
      const items: AccessEvent[] = [];
      let nextToken: string | null | undefined;

      // no `limit` is passed on purpose, so each page comes back as large as the API allows —
      // that keeps the number of round trips (and therefore the total fetch time) as low as possible
      do {
        let data;
        try {
          ({ data } = await client.query({
            query: GET_ACCESS_EVENTS_FOR_TENANT_TYPED,
            variables: {
              nextToken: nextToken ?? undefined,
              filter: {
                actions: [MobileOpCode.AccessGranted, MobileOpCode.AccessDenied],
                dateRange
              }
            }
          }));
        } catch (error) {
          // a cancelled run must not report an error against a hook that is already gone
          if (cancelled) return;
          handleFetchError(translation.access_events_error_fetching);
          console.error(error);
          setLoading(false);
          return;
        }

        // checked before anything else so a cancelled run neither requests the next page nor
        // reports an error against a hook that is already gone
        if (cancelled) return;

        if (!data) break;

        items.push(...data.getAccessEventsForTenant.items);
        nextToken = data.getAccessEventsForTenant.nextToken;
      } while (nextToken);

      setAccessEvents(items);
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // one bucket per slot, oldest first, so quiet slots still render as a point on the zero line
  const accessEventBuckets = useMemo<AccessEventBucket[]>(() => {
    const windowStart = getWindowStart();
    const buckets = Array.from({ length: BUCKET_COUNT }, (_, index) => ({
      timestamp: windowStart.add(index * BUCKET_MINUTES, 'minute'),
      granted: 0,
      denied: 0
    }));

    accessEvents.forEach(({ createdAt, action }) => {
      // a fractional diff keeps `Math.floor` correct for events that predate the window, which an
      // integer diff would truncate towards zero and drop into the very first bucket
      const minutesIntoWindow = dayjs(createdAt).diff(windowStart, 'minute', true);
      const bucketIndex = Math.floor(minutesIntoWindow / BUCKET_MINUTES);
      // events outside the window (e.g. the API returning a wider range) are ignored rather than miscounted
      if (bucketIndex < 0 || bucketIndex >= BUCKET_COUNT) return;

      if (action === MobileOpCode.AccessGranted) {
        buckets[bucketIndex].granted += 1;
      } else if (action === MobileOpCode.AccessDenied) {
        buckets[bucketIndex].denied += 1;
      }
    });

    return buckets.map(({ timestamp, granted, denied }) => ({ timestamp: timestamp.format(), granted, denied }));
  }, [accessEvents]);

  const totalGranted = useMemo(
    () => accessEventBuckets.reduce((sum, { granted }) => sum + granted, 0),
    [accessEventBuckets]
  );

  const totalDenied = useMemo(
    () => accessEventBuckets.reduce((sum, { denied }) => sum + denied, 0),
    [accessEventBuckets]
  );

  return {
    accessEventBuckets,
    totalGranted,
    totalDenied,
    loading
  };
};

export default useRecentAccessEvents;
